import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../features/auth/context';
import { useToast } from '../hooks/useToast';
import LoadingSpinner from '../components/LoadingSpinner';
import FriendForm from '../components/FriendForm';
import ConfirmationDialog from '../components/ConfirmationDialog';
import MiniButton from '../components/MiniButton';
import { authApi } from '../features/auth/api';
import Table from '../components/ui/table';
import type { Column } from '../components/ui/table';
import Button from '../components/ui/button';
import PageContainer from '../components/PageContainer';
import FriendsMap from '../features/maps/FriendsMap';
import { useDocumentTitle } from '../shared/useDocumentTitle';

/**
 * Friend interface matching the backend schema
 */
interface Friend {
  id: string;
  name: string;
  email?: string;
  city: string;
  country: string;
  latitude?: number;
  longitude?: number;
  comment?: string;
  visitOnNextJourney: 'YES' | 'MAYBE' | 'NO';
  createdAt: string;
  updatedAt: string;
}

/**
 * FriendsPage component
 * Displays a table of user's friends with CRUD operations
 * Uses toast notifications for all user feedback
 * Requires authentication to access
 */
const FriendsPage: React.FC = () => {
  useDocumentTitle('Friends to visit');
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [friends, setFriends] = useState<Friend[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingFriend, setEditingFriend] = useState<Friend | null>(null);
  const [deletingFriend, setDeletingFriend] = useState<Friend | null>(null);
  const { showToast } = useToast();

  // Redirect non-authenticated users
  useEffect(() => {
    if (!authLoading && !user) {
      showToast({
        title: 'Authentication Required',
        message: 'Please log in to access your friends list.',
        type: 'info',
        duration: 5000
      });
      navigate('/login');
    }
  }, [user, authLoading, navigate, showToast]);

  /**
   * Fetch friends from the API using authenticated user ID
   */
  const fetchFriends = useCallback(async () => {
    if (!user) return;

    try {
      setLoading(true);
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5050/api';
      console.log('Fetching friends from:', `${API_URL}/friends`);
      console.log('User ID:', user.id);
      
      const response = await authApi.get('/friends');

      console.log('Response status:', response.status);
      console.log('Response ok:', response.status === 200);

      if (response.status !== 200) {
        const errorText = response.data;
        console.log('Error response:', errorText);
        throw new Error('Failed to fetch friends');
      }

      const data = response.data;
      console.log('Friends data:', data);
      setFriends(data.friends || []);
    } catch (err: any) {
      console.error('Fetch friends error:', err);
      setError(err.message);
      showToast({
        title: 'Error',
        message: 'Failed to load friends. Please try again.',
        type: 'error',
        duration: 6000
      });
    } finally {
      setLoading(false);
    }
  }, [user, showToast]);

  /**
   * Create or update a friend using authenticated user ID
   */
  const saveFriend = async (friendData: Omit<Friend, 'id' | 'createdAt' | 'updatedAt'>) => {
    if (!user) return;

    try {
      const url = editingFriend 
        ? `/friends/${editingFriend.id}`
        : '/friends';
      const method = editingFriend ? 'put' : 'post';
      const response = await authApi[method](url, friendData);

      if (response.status !== 200 && response.status !== 201) {
        const errorData = response.data;
        throw new Error(errorData.error?.message || 'Failed to save friend');
      }

      const data = response.data;
      
      if (editingFriend) {
        setFriends(friends.map(f => f.id === editingFriend.id ? data.friend : f));
        showToast({
          title: 'Success',
          message: 'Friend updated successfully!',
          type: 'success',
          duration: 4000
        });
      } else {
        setFriends([...friends, data.friend]);
        showToast({
          title: 'Success',
          message: 'Friend added successfully!',
          type: 'success',
          duration: 4000
        });
      }

      setShowAddForm(false);
      setEditingFriend(null);
    } catch (err: any) {
      showToast({
        title: 'Error',
        message: err.message || 'Failed to save friend. Please try again.',
        type: 'error',
        duration: 6000
      });
    }
  };

  /**
   * Delete a friend using authenticated user ID
   */
  const deleteFriend = async (friend: Friend) => {
    if (!user) return;

    try {
      const response = await authApi.delete(`/friends/${friend.id}`);

      if (response.status !== 200 && response.status !== 204) {
        throw new Error('Failed to delete friend');
      }

      setFriends(friends.filter(f => f.id !== friend.id));
      showToast({
        title: 'Success',
        message: 'Friend deleted successfully!',
        type: 'success',
        duration: 4000
      });
    } catch (err: any) {
      showToast({
        title: 'Error',
        message: 'Failed to delete friend. Please try again.',
        type: 'error',
        duration: 6000
      });
    } finally {
      setDeletingFriend(null);
    }
  };

  /**
   * Handle friend deletion with confirmation
   */
  const handleDeleteFriend = (friend: Friend) => {
    setDeletingFriend(friend);
  };

  /**
   * Confirm friend deletion
   */
  const confirmDelete = () => {
    if (deletingFriend) {
      deleteFriend(deletingFriend);
    }
  };

  /**
   * Cancel friend deletion
   */
  const cancelDelete = () => {
    setDeletingFriend(null);
  };

  /**
   * Start editing a friend
   */
  const handleEditFriend = (friend: Friend) => {
    setEditingFriend(friend);
    setShowAddForm(true);
  };

  /**
   * Cancel editing
   */
  const cancelEdit = () => {
    setEditingFriend(null);
    setShowAddForm(false);
  };

  // Load friends when user is authenticated
  useEffect(() => {
    if (user && !authLoading) {
      fetchFriends();
    }
  }, [user, authLoading, fetchFriends]);

  // Show loading while checking authentication
  if (authLoading) {
    return (
      <PageContainer>
        <LoadingSpinner message="Checking authentication..." />
      </PageContainer>
    );
  }

  // Don't render anything if user is not authenticated (will redirect)
  if (!user) {
    return null;
  }

  if (loading) {
    return (
      <PageContainer>
        <LoadingSpinner message="Loading your friends..." />
      </PageContainer>
    );
  }

  // Reusable table columns
  const columns: Column<Friend>[] = [
    { key: 'name', header: 'Name', className: "max-w-[18ch] sm:max-w-[40ch] sticky left-0 bg-[var(--surface)] whitespace-normal break-words", render: (f) => (
      <div className="text-left whitespace-normal break-words">
        <div className="mb-1">
          <span className="text-emerald-700 font-medium">{f.name}</span>
        </div>
        <div className="flex gap-1">
          <MiniButton
            label="Edit"
            onClick={() => handleEditFriend(f)}
            hoverFillColor="#dcfce7"
            hoverOutlineColor="#bbf7d0"
            hoverTextColor="#166534"
          />
          <MiniButton
            label="Delete"
            onClick={() => handleDeleteFriend(f)}
          />
        </div>
      </div>
    ) },
    { key: 'location', header: 'Location', render: (f) => `${f.city}, ${f.country}` },
    { key: 'visitOnNextJourney', header: 'Visit on Next Journey', render: (f) => (
      <span style={{
        padding: '0.25rem 0.5rem',
        borderRadius: 4,
        fontSize: 12,
        fontWeight: 700,
        backgroundColor: f.visitOnNextJourney === 'YES' ? '#d4edda' : (f.visitOnNextJourney === 'MAYBE' ? '#fff3cd' : '#f8d7da'),
        color: f.visitOnNextJourney === 'YES' ? '#155724' : (f.visitOnNextJourney === 'MAYBE' ? '#856404' : '#721c24')
      }}>
        {f.visitOnNextJourney}
      </span>
    ) },
    { key: 'comment', header: 'Comment', render: (f) => f.comment || '-' },
  ];

  return (
    <PageContainer style={{ fontFamily: 'sans-serif' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: 'var(--text)' }}>Friends to visit</h1>
        <Button variant="greenPrimary" onClick={() => setShowAddForm(true)}>
          Add Friend
        </Button>
      </div>

      {error && (
        <div style={{ color: 'red', marginBottom: '1rem', padding: '1rem', backgroundColor: '#fdeaea', borderRadius: '4px' }}>
          {error}
        </div>
      )}

      {/* Friends Map */}
      <div style={{ marginBottom: '1.5rem' }}>
        <FriendsMap friends={friends} />
      </div>

      {/* Friends Table */}
      <Table columns={columns} data={friends} emptyMessage={'No friends added yet. Click "Add Friend" to get started!'} />

      {/* Add/Edit Friend Form */}
      {showAddForm && (
        <FriendForm
          friend={editingFriend}
          onSave={saveFriend}
          onCancel={cancelEdit}
        />
      )}

      {/* Delete Confirmation Dialog */}
      {deletingFriend && (
        <ConfirmationDialog
          title="Delete Friend"
          message={`Are you sure you want to delete ${deletingFriend.name}? This action cannot be undone.`}
          onConfirm={confirmDelete}
          onCancel={cancelDelete}
        />
      )}
    </PageContainer>
  );
};

export default FriendsPage; 